import React, { useState, useMemo, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { useTranslation } from "react-i18next";

import { useDarkMode } from "@hooks/dark-mode";
import Allowance2 from "../../../assets/images/icons/Allowance2.png";
import Co2Iphone from "../../../assets/images/icons/emissions.png";
import timeReg from "../../../assets/images/icons/time.png";

// Animation Variants
const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
        opacity: 1,
        transition: {
            staggerChildren: 0.1,
            delayChildren: 0.2
        }
    }
};

const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
        y: 0,
        opacity: 1,
        transition: { type: "spring", stiffness: 100 }
    }
};

const contentVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.4, ease: "easeInOut" } },
    exit: { opacity: 0, y: -30, transition: { duration: 0.3, ease: "easeInOut" } }
};

// Define types for bullet points
interface BulletPoint {
    title: string;
    text: string;
}

interface FeatureData {
    id: string;
    title: string;
    description: string;
    bulletPoints: BulletPoint[];
    additionalInfo: string;
}

// Products Component - Interactive Design
export default function Products() {
    const { t } = useTranslation();
    const { dark } = useDarkMode();
    const featureColor = dark ? '#80ABF9' : '#1673FF';

    // Create feature data using useMemo to update when translations change
    const featuresData = useMemo(() => [
        {
            id: "tracking",
            title: t('home.productsSolutions.products.tracking.title'),
            description: t('home.productsSolutions.products.tracking.description'),
            bulletPoints: [
                {
                    title: t('home.productsSolutions.products.tracking.bulletPoints.0.title'),
                    text: t('home.productsSolutions.products.tracking.bulletPoints.0.text')
                },
                {
                    title: t('home.productsSolutions.products.tracking.bulletPoints.1.title'),
                    text: t('home.productsSolutions.products.tracking.bulletPoints.1.text')
                },
                {
                    title: t('home.productsSolutions.products.tracking.bulletPoints.2.title'),
                    text: t('home.productsSolutions.products.tracking.bulletPoints.2.text')
                },
                {
                    title: t('home.productsSolutions.products.tracking.bulletPoints.3.title'),
                    text: t('home.productsSolutions.products.tracking.bulletPoints.3.text')
                }
            ],
            additionalInfo: t('home.productsSolutions.products.tracking.additionalInfo')
        },
        {
            id: "compliance",
            title: t('home.productsSolutions.products.driving.title'),
            description: t('home.productsSolutions.products.driving.description'),
            bulletPoints: [
                {
                    title: t('home.productsSolutions.products.driving.bulletPoints.0.title'),
                    text: t('home.productsSolutions.products.driving.bulletPoints.0.text')
                },
                {
                    title: t('home.productsSolutions.products.driving.bulletPoints.1.title'),
                    text: t('home.productsSolutions.products.driving.bulletPoints.1.text')
                },
                {
                    title: t('home.productsSolutions.products.driving.bulletPoints.2.title'),
                    text: t('home.productsSolutions.products.driving.bulletPoints.2.text')
                },
                {
                    title: t('home.productsSolutions.products.driving.bulletPoints.3.title'),
                    text: t('home.productsSolutions.products.driving.bulletPoints.3.text')
                }
            ],
            additionalInfo: t('home.productsSolutions.products.driving.additionalInfo')
        },
        {
            id: "integration",
            title: t('home.productsSolutions.products.timeRegistration.title'),
            description: t('home.productsSolutions.products.timeRegistration.description'),
            bulletPoints: [
                {
                    title: t('home.productsSolutions.products.timeRegistration.bulletPoints.0.title'),
                    text: t('home.productsSolutions.products.timeRegistration.bulletPoints.0.text')
                },
                {
                    title: t('home.productsSolutions.products.timeRegistration.bulletPoints.1.title'),
                    text: t('home.productsSolutions.products.timeRegistration.bulletPoints.1.text')
                },
                {
                    title: t('home.productsSolutions.products.timeRegistration.bulletPoints.2.title'),
                    text: t('home.productsSolutions.products.timeRegistration.bulletPoints.2.text')
                },
                {
                    title: t('home.productsSolutions.products.timeRegistration.bulletPoints.3.title'),
                    text: t('home.productsSolutions.products.timeRegistration.bulletPoints.3.text')
                }
            ],
            additionalInfo: t('home.productsSolutions.products.timeRegistration.additionalInfo')
        }
    ], [t]);

    const [selectedFeature, setSelectedFeature] = useState<FeatureData>(featuresData[0]);

    // Make sure selectedFeature updates when language changes
    useEffect(() => {
        const currentFeatureId = selectedFeature.id;
        const updatedFeature = featuresData.find(feature => feature.id === currentFeatureId) || featuresData[0];
        setSelectedFeature(updatedFeature);
    }, [featuresData, selectedFeature.id]);

    return (
        <section className="pb-16 sm:py-24 relative" style={{ backgroundColor: dark ? "#28282B" : "#F0F0F0" }}>
            <motion.section
                id="solutions"
                variants={containerVariants}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.1 }}
            >
                <div className="px-4 mx-auto max-w-screen-xl">
                    {/* Header Text */}
                    <motion.div className="text-center pt-8 mb-12 md:mb-16" variants={itemVariants}>
                        <h2 className="mb-4 text-4xl md:text-5xl tracking-tight font-extrabold ">
                            {t('home.productsSolutions.title')}
                        </h2>
                        <p className="text-lg md:text-xl font-light text-gray-600 dark:text-gray-400 max-w-3xl mx-auto">
                            {t('home.productsSolutions.subtitle')}
                        </p>
                    </motion.div>

                    {/* Interactive Section */}
                    <div className="lg:grid lg:grid-cols-3 lg:gap-12 xl:gap-16 items-start">
                        {/* Left Column: Feature Selectors */}
                        <motion.div
                            className="mb-10 lg:mb-0 lg:col-span-1 flex flex-col space-y-4"
                            variants={containerVariants}
                        >
                            {featuresData.map((feature) => (
                                <motion.button
                                    key={feature.id}
                                    onClick={() => setSelectedFeature(feature)}
                                    className={`py-4 p-4 pl-6 rounded-lg text-left w-full transition-colors duration-200 ${selectedFeature.id === feature.id
                                        ? 'bg-secBg'
                                        : 'bg-secBg/50'
                                        }`}
                                    variants={itemVariants}
                                >
                                    <span className={`block font-semibold text-lg ${selectedFeature.id === feature.id ? 'text-blue-600 dark:text-blue-400' : ''}`}>
                                        {feature.title}
                                    </span>
                                </motion.button>
                            ))}
                        </motion.div>

                        {/* Right Column: Content Display */}
                        <div className="col-span-2 ml-6">
                            <AnimatePresence mode="wait">
                                <motion.div
                                    key={selectedFeature.id}
                                    className="flex flex-col md:flex-row items-start md:items-start gap-8 md:gap-12 lg:gap-16"
                                    variants={contentVariants}
                                    initial="hidden"
                                    whileInView="visible"
                                    exit="exit"
                                    viewport={{ once: true, amount: 0.3 }}
                                >
                                    {/* Text Description - Left Side */}
                                    <div className="flex-1 max-w-3xl">
                                        <h3 className="text-2xl font-semibold mb-3">
                                            {selectedFeature.title}
                                        </h3>
                                        <p className="leading-relaxed mb-4">
                                            {selectedFeature.description}
                                        </p>

                                        {/* Bullet Points */}
                                        <div className="grid gap-3 mt-4 mb-4">
                                            {selectedFeature.bulletPoints.map((point, idx) => (
                                                <div key={idx} className="flex items-start gap-2">
                                                    <CheckCircleOutlineIcon sx={{ color: featureColor }} />
                                                    <p className="font-light text-gray-700 md:text-lg dark:text-gray-300">
                                                        <strong style={{ color: featureColor }}>{point.title}</strong> {point.text}
                                                    </p>
                                                </div>
                                            ))}
                                        </div>

                                        {/* Additional Description */}
                                        {selectedFeature.additionalInfo && (
                                            <div>
                                                <p className="leading-relaxed">
                                                    {selectedFeature.additionalInfo}
                                                </p>
                                            </div>
                                        )}
                                    </div>

                                    {/* Image - Right Side */}
                                    <div className="flex justify-center w-full md:w-auto">
                                        <motion.img
                                            src={selectedFeature.id === "tracking" ? Co2Iphone :
                                                selectedFeature.id === "integration" ? timeReg :
                                                    Allowance2}
                                            style={{
                                                maxHeight: "600px",
                                                height: "auto",
                                                maxWidth: "100%"
                                            }}
                                            className="w-auto max-h-[400px] md:max-h-[600px]"
                                            alt={selectedFeature.title}
                                            initial={{ opacity: 0, y: 20 }}
                                            whileInView={{ opacity: 1, y: 0 }}
                                            viewport={{ once: true, amount: 0.3 }}
                                            transition={{ duration: 0.5 }}
                                        />
                                    </div>
                                </motion.div>
                            </AnimatePresence>
                        </div>
                    </div>
                </div>
            </motion.section>
        </section>
    );
}